'use client';

import type { EmployeeWithStats } from './types';

interface EmployeeCardProps {
  employee: EmployeeWithStats;
  onClick?: (employee: EmployeeWithStats) => void;
}

const STATUS_CONFIG: Record<string, { dot: string; label: string }> = {
  online:  { dot: '#16a34a', label: 'Online' },
  away:    { dot: '#F5A623', label: 'Away' },
  offline: { dot: '#9ca3af', label: 'Offline' },
};

const renderStarRating = (rating: number | null): JSX.Element => {
  if (rating === null) {
    return <span className="text-[10px] text-gray-400">—</span>;
  }
  return (
    <div className="flex items-center gap-0.5" aria-label={`Rating: ${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map(i => {
        const filled = rating >= i;
        const half = !filled && rating >= i - 0.5;
        return (
          <svg
            key={i}
            className="w-3 h-3"
            viewBox="0 0 24 24"
            fill={filled ? '#F5A623' : half ? 'url(#halfgrad)' : '#e5e7eb'}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="halfgrad">
                <stop offset="50%" stopColor="#F5A623" />
                <stop offset="50%" stopColor="#e5e7eb" />
              </linearGradient>
            </defs>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01z" />
          </svg>
        );
      })}
      <span className="text-[10px] text-gray-500 ml-1">{rating.toFixed(1)}</span>
    </div>
  );
};

const EmployeeCard = ({ employee, onClick }: EmployeeCardProps) => {
  const initials = `${employee.first_name?.[0] ?? '?'}${employee.last_name?.[0] ?? '?'}`.toUpperCase();
  const displayName = employee.preferred_name
    ? `${employee.preferred_name} ${employee.last_name}`
    : `${employee.first_name} ${employee.last_name}`;
  const statusCfg = STATUS_CONFIG[employee.status] ?? STATUS_CONFIG.offline;

  const isTerminated = !!employee.termination_date || employee.employment_type === 'terminated';

  // Generate a consistent avatar background color from initials
  const colors = ['#1C2E5E', '#E89A3C', '#7c3aed', '#16a34a', '#dc2626', '#F5A623', '#0891b2', '#be185d'];
  const colorIndex = (employee.first_name?.charCodeAt(0) ?? 0) % colors.length;
  const avatarBg = colors[colorIndex];

  return (
    <button
      type="button"
      onClick={() => onClick?.(employee)}
      className={`w-full text-left bg-white border border-gray-100 hover:border-[#E89A3C] hover:shadow-md shadow-sm rounded-2xl p-4 transition-all group ${
        isTerminated ? 'opacity-60' : ''
      }`}
      style={{ cursor: 'pointer' }}
      aria-label={`View ${displayName}'s profile`}
    >
      {/* Top: avatar + status indicator */}
      <div className="flex items-start gap-3 mb-3">
        <div className="relative flex-shrink-0">
          {employee.avatar_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={employee.avatar_url}
              alt=""
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center text-white text-sm font-bold"
              style={{ background: avatarBg }}
            >
              {initials}
            </div>
          )}
          {/* Status dot */}
          <span
            className="absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white"
            style={{ background: statusCfg.dot }}
            aria-label={statusCfg.label}
          />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-bold text-[#1C2E5E] truncate" title={displayName}>{displayName}</p>
          <p className="text-[11px] text-gray-600 truncate" title={employee.job_title}>{employee.job_title}</p>
          <p className="text-[10px] text-gray-400 truncate mt-0.5" title={employee.department}>{employee.department}</p>
        </div>
      </div>

      {/* Bottom: rating + cert pills + tenure */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-2">
          {renderStarRating(employee.latest_review_rating)}
        </div>
        <div className="flex items-center gap-1">
          {employee.expiring_certs_count > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700"
              title={`${employee.expiring_certs_count} certs expiring soon`}
            >
              ⚠ {employee.expiring_certs_count}
            </span>
          )}
          {employee.pending_reviews_count > 0 && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-100 text-red-700"
              title={`${employee.pending_reviews_count} pending reviews`}
            >
              📋 {employee.pending_reviews_count}
            </span>
          )}
          {isTerminated && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-gray-200 text-gray-600">
              Terminated
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default EmployeeCard;
